import { UserRequestHistoryItem } from "src/api/fetchUserRequestHistory";

/** UI segment keys for history drawer menu / detail views */
export type HistoryEndpointKey =
  | "waiver"
  | "start"
  | "team"
  | "draft"
  | "trade"
  | "rankings"
  | "news"
  | "rosters";

const ENDPOINT_LABELS: Record<HistoryEndpointKey, string> = {
  waiver: "Waivers",
  start: "Start/Sit",
  team: "Team Analysis",
  draft: "Draft Advice",
  trade: "Trade",
  rankings: "Rankings",
  news: "News",
  rosters: "Rosters",
};

/**
 * Map history API endpoint / endpoint_name → UI key.
 * Returns null for preload/internal endpoints that must not appear in the drawer.
 */
export function mapHistoryEndpointToKey(
  endpoint?: string | null,
  endpointName?: string | null
): HistoryEndpointKey | null {
  const e = `${endpointName || ""} ${endpoint || ""}`.toLowerCase();
  if (!e.trim()) return null;

  // Exclude common + super preload
  if (e.includes("common_league_data") || e.includes("super_analysis")) return null;

  // Trade before "start" — avoid false matches
  if (e.includes("analyze_trade") || e.includes("trade")) return "trade";
  if (e.includes("waiver")) return "waiver";
  if (e.includes("start_streaming") || e === "start" || e.endsWith("/start") || e.includes("/start_") || e.includes(" start") || endpointName?.toLowerCase() === "start") {
    return "start";
  }
  if (e.includes("rundown") || e.includes("team")) return "team";
  if (e.includes("draft")) return "draft";
  if (e.includes("all_rankings") || e.includes("rankings")) return "rankings";
  if (e.includes("news")) return "news";
  if (e.includes("roster")) return "rosters";

  return null;
}

export function getHistoryEndpointLabel(key: HistoryEndpointKey): string {
  return ENDPOINT_LABELS[key];
}

/**
 * History timestamps → en-US local time; MM-DD-YY with hyphens + h:mm AM/PM.
 * POC often sends UTC without Z — append Z so we convert UTC → user's local timezone.
 */
export function formatHistoryTimestampLocal(iso?: string | null): string | null {
  if (!iso) return null;
  const hasTz = /([zZ]|[+-]\d{2}:?\d{2})$/.test(iso);
  const d = new Date(hasTz ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return null;
  return d
    .toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "2-digit",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .replace(/\//g, "-")
    .replace(",", "");
}

export interface HistoryMenuOption {
  key: HistoryEndpointKey;
  label: string;
}

/** True when item has usable response text (skip null/empty content in History UI). */
export function hasHistoryContent(item: UserRequestHistoryItem): boolean {
  const text = item.content ?? item.response;
  return typeof text === "string" && text.trim().length > 0;
}

/** Unique endpoints present in items with content, ordered by most recent occurrence first. */
export function getHistoryMenuOptions(items: UserRequestHistoryItem[]): HistoryMenuOption[] {
  const seen = new Set<HistoryEndpointKey>();
  const options: HistoryMenuOption[] = [];

  // API returns desc (newest first) — keep that order for menu discovery
  for (const item of items) {
    if (!hasHistoryContent(item)) continue;
    const key = mapHistoryEndpointToKey(item.endpoint, item.endpoint_name);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    options.push({ key, label: getHistoryEndpointLabel(key) });
  }

  return options;
}

/** Items for one endpoint key with content, newest → oldest. */
export function filterHistoryItemsByKey(
  items: UserRequestHistoryItem[],
  key: HistoryEndpointKey
): UserRequestHistoryItem[] {
  return items
    .filter(
      (item) =>
        hasHistoryContent(item) &&
        mapHistoryEndpointToKey(item.endpoint, item.endpoint_name) === key
    )
    .sort((a, b) => {
      const ta = a.timestamp ? new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(a.timestamp) ? a.timestamp : `${a.timestamp}Z`).getTime() : 0;
      const tb = b.timestamp ? new Date(/([zZ]|[+-]\d{2}:?\d{2})$/.test(b.timestamp) ? b.timestamp : `${b.timestamp}Z`).getTime() : 0;
      return tb - ta;
    });
}

export function getHistoryItemContent(item: UserRequestHistoryItem): string {
  return item.content || item.response || "No content available for this request.";
}

/**
 * History API returns charts as { [chart_id]: ChartData } (or null).
 * UI expects ChartData[] ordered by [CHART:id] placeholders in content.
 */
export function normalizeHistoryCharts(
  content: string | undefined,
  charts: Record<string, unknown> | unknown[] | null | undefined
): unknown[] {
  if (!charts) return [];
  if (Array.isArray(charts)) return charts;

  const placeholders = (content || "").match(/\[CHART:([^\]]+)\]/g) || [];
  if (placeholders.length === 0) return Object.values(charts);

  return placeholders
    .map((ph) => {
      const id = ph.slice("[CHART:".length, -1);
      return charts[id];
    })
    .filter(Boolean);
}
